// apps/hyperseguros-web/src/components/Footer/index.tsx

'use client'

import React from 'react'
import Link from 'next/link'
import { Box, useTheme } from '@mui/material'
import type { TNavConfig, TNavMenu, TNavMenuLink } from '../Nav/types'
import type { TFooterLegal } from './types'

export type { TFooterLegal, TFooterLink } from './types'

type FooterProps = {
  navConfig: TNavConfig
  legal: TFooterLegal
}

/** A headed nav group, tagged with its menu when it opens that menu's run. */
type TFooterBlock = {
  key: string
  eyebrow?: string
  heading: string
  items: TNavMenuLink[]
}

const isFlat = (menu: TNavMenu) => menu.groups.every((group) => !group.heading)

/**
 * Renders the site's full navigation, expanded, as the footer. The sections
 * are never redeclared here: this reads the exact same `TNavConfig` passed
 * to `<Nav>`, so a section only has to be defined once (in `buildNavConfig`).
 *
 * Layout: the headed groups of every menu flow, in nav order, through CSS
 * multi-columns that balance their heights, so a menu with many groups no
 * longer dictates the footer's height; each group is an unbreakable block and
 * the first block of a menu carries the menu's name as an eyebrow (a
 * heading-less group in such a menu is titled with the menu's name). Menus
 * whose groups have no headings (flat lists such as Industrias) follow as
 * their own full-width band, the label above the links in short columns.
 */
export default function Footer({ navConfig, legal }: FooterProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const headingColor = isDark ? '#f5f5f7' : '#1d1d1f'
  const mutedColor = isDark ? '#86868b' : '#6e6e73'
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'

  const menus = navConfig.items.filter(
    (item): item is TNavMenu => item.type === 'menu'
  )
  // A heading-less group inside a headed menu is titled with the menu label
  // itself, so the eyebrow is only needed when the menu opens with a heading.
  const blocks: TFooterBlock[] = menus
    .filter((menu) => !isFlat(menu))
    .flatMap((menu) =>
      menu.groups.map((group, index) => ({
        key: `${menu.label}-${group.heading ?? index}`,
        eyebrow: index === 0 && group.heading ? menu.label : undefined,
        heading: group.heading ?? menu.label,
        items: group.items,
      }))
    )
  const flatMenus = menus.filter(isFlat)

  const linkStyle: React.CSSProperties = {
    color: mutedColor,
    textDecoration: 'none',
    fontSize: '12px',
  }
  const underlineOnHover = {
    onMouseEnter: (e: React.MouseEvent<globalThis.HTMLAnchorElement>) => {
      e.currentTarget.style.textDecoration = 'underline'
    },
    onMouseLeave: (e: React.MouseEvent<globalThis.HTMLAnchorElement>) => {
      e.currentTarget.style.textDecoration = 'none'
    },
  }
  const headingSx = {
    fontSize: '12px',
    fontWeight: 600,
    color: headingColor,
    margin: 0,
    marginBottom: '8px',
  }

  const renderLinks = (items: TNavMenuLink[]) => (
    <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((item) => (
        <Box component="li" key={item.href} sx={{ marginBottom: '8px' }}>
          <Link href={item.href} style={linkStyle} {...underlineOnHover}>
            {item.label}
          </Link>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        borderTop: `1px solid ${borderColor}`,
        backgroundColor: isDark ? '#000000' : '#f5f5f7',
        color: mutedColor,
      }}
    >
      <Box
        sx={{
          maxWidth: '1024px',
          mx: 'auto',
          px: { xs: '16px', md: '24px' },
          py: { xs: '32px', md: '40px' },
        }}
      >
        {/* Headed groups, balanced across as many columns as fit. */}
        <Box sx={{ columnWidth: '160px', columnGap: '24px' }}>
          {blocks.map((block) => (
            <Box
              key={block.key}
              sx={{
                breakInside: 'avoid',
                // Keeps the block's margin inside its column in every engine.
                display: 'inline-block',
                width: '100%',
                marginBottom: '20px',
              }}
            >
              {block.eyebrow && (
                <Box
                  component="p"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    // Not opacity-dimmed: opacity 0.6 on mutedColor fell to
                    // ~2.28:1 here, below the 4.5:1 small-text requirement.
                    color: mutedColor,
                    margin: 0,
                    marginBottom: '4px',
                  }}
                >
                  {block.eyebrow}
                </Box>
              )}
              <Box component="p" sx={headingSx}>
                {block.heading}
              </Box>
              {renderLinks(block.items)}
            </Box>
          ))}
        </Box>

        {/* Flat menus: one band each, the label above short columns. */}
        {flatMenus.map((menu) => (
          <Box
            key={menu.label}
            sx={{
              borderTop: `1px solid ${borderColor}`,
              marginTop: '12px',
              paddingTop: '24px',
              marginBottom: '12px',
            }}
          >
            <Box component="p" sx={headingSx}>
              {menu.label}
            </Box>
            <Box
              sx={{
                columnCount: { xs: 2, sm: 3, md: 4 },
                columnGap: '24px',
                '& li': { breakInside: 'avoid' },
              }}
            >
              {renderLinks(menu.groups.flatMap((group) => group.items))}
            </Box>
          </Box>
        ))}

        <Box
          sx={{ borderTop: `1px solid ${borderColor}`, mt: '16px', pt: '16px' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <Box component="p" sx={{ fontSize: '12px' }}>
              {legal.copyright}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
              }}
            >
              {legal.legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  style={linkStyle}
                  {...underlineOnHover}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
